import { StreamingEventBus } from 'streaming/abstract/StreamingEventBus';
import { Logger, LogSender } from '../../../logger';
import StreamingError from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingSubscription, {
  StreamingSubscriptionEventHandler,
} from '../../abstract/StreamingSubscription';
import {
  AlertRequestPayload,
  AlertSubscribeRequest,
  AlertSubscribeResponse,
  AlertUpdateResponse,
  getAlertSubscribeRequest,
} from './Messages';

export type AlertOnSubscribe = (
  msg: AlertSubscribeResponse,
  err: StreamingError
) => void;
export type AlertOnUpdate = (
  msg: AlertUpdateResponse,
  err: StreamingError
) => void;

export default class AlertSubscription extends StreamingSubscription<
  AlertRequestPayload,
  AlertSubscribeRequest
> {
  public get subscribed(): AlertRequestPayload {
    return {};
  }

  public get SubscribeRequest(): AlertSubscribeRequest {
    return getAlertSubscribeRequest();
  }

  public readonly on: {
    [DoryMessageType.SUBSCRIBE]: StreamingSubscriptionEventHandler<AlertSubscribeResponse>;
    [DoryMessageType.UPDATE]: StreamingSubscriptionEventHandler<AlertUpdateResponse>;
  } = {
    /**
     * The handler for "subscribe" has to be called when:
     * - a message of with messageType "subscribe" is received and messageId equal to this.id
     *
     * The handler is stopped when:
     * - it's called
     */
    [DoryMessageType.SUBSCRIBE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.SUBSCRIBE].isAttached = true;
        this.on[DoryMessageType.SUBSCRIBE].detach =
          this.eventBus.once<AlertSubscribeResponse>(
            DoryServiceType.ALERT,
            DoryMessageType.SUBSCRIBE,
            (data) => {
              this.on[DoryMessageType.SUBSCRIBE].handler(data, undefined);
              this.on[DoryMessageType.SUBSCRIBE].isAttached = false;
              this.stopTimeout();
            }
          );
      },
    },
    /**
     * The handler for "update" has to be called when:
     * - a message of with messageType "update" is received that contains one of the subscribed rics
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.UPDATE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.UPDATE].isAttached = true;
        this.on[DoryMessageType.UPDATE].detach =
          this.eventBus.on<AlertUpdateResponse>(
            DoryServiceType.ALERT,
            DoryMessageType.UPDATE,
            (data) => {
              this.on[DoryMessageType.UPDATE].handler(data, undefined);
            }
          );
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    onSubscribe: AlertOnSubscribe,
    onUpdate: AlertOnUpdate
  ) {
    super(id, eventBus);

    this.on[DoryMessageType.SUBSCRIBE].handler = onSubscribe;
    this.on[DoryMessageType.UPDATE].handler = onUpdate;
  }

  public start(timeout: number): void {
    // Attach all event listeners
    this.on[DoryMessageType.SUBSCRIBE].attach();
    this.on[DoryMessageType.UPDATE].attach();
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.Alert.AlertSubscription.start',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public restart(timeout: number): void {
    // Attach all event listeners that might be detached
    if (!this.on[DoryMessageType.SUBSCRIBE].isAttached) {
      this.on[DoryMessageType.SUBSCRIBE].attach();
    }
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.Alert.AlertSubscription.restart',
      this.on[DoryMessageType.SUBSCRIBE].handler
    );
  }
  public stop(): void {
    // Stop all event listeners
    if (this.on[DoryMessageType.SUBSCRIBE].detach) {
      this.on[DoryMessageType.SUBSCRIBE].detach();
      Logger.silly(LogSender.STREAMING, 'Detached Subscribe listener');
    }
    if (this.on[DoryMessageType.UPDATE].detach) {
      this.on[DoryMessageType.UPDATE].detach();
      Logger.silly(LogSender.STREAMING, 'Detached Update listener');
    }
    // Stop pending timeout
    this.stopTimeout();
  }
}
